import { useRect } from '@studio-freight/hamo'
import cn from 'clsx'


import { Image } from 'components/image'
import { Button } from 'components/button'
import { ListItem } from 'components/list-item'
import { projects } from 'content/projects'
import { useScroll } from 'hooks/use-scroll'
import { Layout } from 'layouts/default'
import { button, useControls } from 'leva'
import { clamp, mapRange } from 'lib/maths'
import { useStore } from 'lib/store'
import dynamic from 'next/dynamic'
import { useEffect, useRef, useState } from 'react'
import { useIntersection, useWindowSize } from 'react-use'
import s from './home.module.scss'
import PartnersMarquee from 'components/partners-marquee'

const Parallax = dynamic(
  () => import('components/parallax').then((mod) => mod.Parallax),
  { ssr: false }
)

const AppearTitle = dynamic(
  () => import('components/appear-title').then((mod) => mod.AppearTitle),
  { ssr: false }
)

const FeatureCards = dynamic(
  () => import('components/feature-cards').then((mod) => mod.FeatureCards),
  { ssr: false }
)

const WebGL = dynamic(
  () => import('components/webgl').then(({ WebGL }) => WebGL),
  { ssr: false }
)

const HeroTextIn = ({ children, introOut }) => {
  return (
    <div className={cn(s['hide-text'], introOut && s['show-text'])}>
      {children}
    </div>
  )
}

export default function Home() {
  const [hasScrolled, setHasScrolled] = useState()
  const zoomRef = useRef(null)
  const [zoomWrapperRectRef, zoomWrapperRect] = useRect()
  const { height: windowHeight } = useWindowSize()
  const introOut = useStore(({ introOut }) => introOut)

  const [theme, setTheme] = useState('dark')
  const lenis = useStore(({ lenis }) => lenis)

  useControls(
    'aryan',
    () => ({
      stop: button(() => {
        lenis.stop()
      }),
      start: button(() => {
        lenis.start()
      }),
    }),
    [lenis]
  )

  useControls(
    'scrollTo',
    () => ({
      immediate: button(() => {
        lenis.scrollTo(30000, { immediate: true })
      }),
      smoothDuration: button(() => {
        lenis.scrollTo(30000, { lock: true, duration: 10 })
      }),
      smooth: button(() => {
        lenis.scrollTo(30000)
      }),
      forceScrollTo: button(() => {
        lenis.scrollTo(30000, { force: true })
      }),
    }),
    [lenis]
  )

  useEffect(() => {
    if (!lenis) return

    function onClassNameChange(lenis) {
      console.log(lenis.className)
    }

    lenis.on('className change', onClassNameChange)

    return () => {
      lenis.off('className change', onClassNameChange)
    }
  }, [lenis])

  useScroll(({ scroll }) => {
    setHasScrolled(scroll > 10)
    if (!zoomWrapperRect.top) return

    const start = zoomWrapperRect.top + windowHeight * 0.5
    const end = zoomWrapperRect.top + zoomWrapperRect.height - windowHeight

    const progress = clamp(0, mapRange(start, end, scroll, 0, 1), 1)
    const center = 0.6
    const progress1 = clamp(0, mapRange(0, center, progress, 0, 1), 1)
    const progress2 = clamp(0, mapRange(center - 0.055, 1, progress, 0, 1), 1)
    setTheme(progress2 === 1 ? 'light' : 'dark')

    zoomRef.current.style.setProperty('--progress1', progress1)
    zoomRef.current.style.setProperty('--progress2', progress2)

    if (progress === 1) {
      zoomRef.current.style.setProperty('background-color', 'currentColor')
    } else {
      zoomRef.current.style.removeProperty('background-image')
      zoomRef.current.style.removeProperty('background-size')
      zoomRef.current.style.removeProperty('background-position')
      zoomRef.current.style.removeProperty('background-color')
    }
  })

  //removed whyRef from the first variable
  const [whyRef, whyRect] = useRect()
  const [cardsRef, cardsRect, cardsRectRef] = useRect()
  const [whiteRect, whiteRef] = useRect()
  const [featuresRef, featuresRect] = useRect()
  const [inuseRef, inuseRect] = useRect()

  const addThreshold = useStore(({ addThreshold }) => addThreshold)

  useEffect(() => {
    addThreshold({ id: 'top', value: 0 })
  }, [])

  useEffect(() => {
    const top = whyRect.top - windowHeight / 2
    addThreshold({ id: 'why-start', value: top })
    addThreshold({
      id: 'why-end',
      value: top + whyRect.height,
    })
  }, [whyRect])

  useEffect(() => {
    const top = cardsRect.top - windowHeight / 2
    addThreshold({ id: 'cards-start', value: top })
    addThreshold({ id: 'cards-end', value: top + cardsRect.height })
    addThreshold({
      id: 'red-end',
      value: top + cardsRect.height + windowHeight,
    })
  }, [cardsRect])

  useEffect(() => {
    const top = whiteRect.top - windowHeight
    addThreshold({ id: 'light-start', value: top })
  }, [whiteRect])

  useEffect(() => {
    const top = featuresRect.top
    addThreshold({ id: 'features', value: top })
  }, [featuresRect])

  useEffect(() => {
    const top = inuseRect.top
    addThreshold({ id: 'in-use', value: top })
  }, [inuseRect])

  useEffect(() => {
    const top = lenis?.limit
    addThreshold({ id: 'end', value: top })
  }, [lenis?.limit])

  useScroll((e) => {
    console.log(window.scrollY, e.scroll, e.isScrolling, e.velocity, e.isLocked)
    // console.log(e.isScrolling)
  })

  const inUseRef = useRef()

  const [visible, setIsVisible] = useState(false)
  const intersection = useIntersection(inUseRef, {
    threshold: 0.2,
  })
  useEffect(() => {
    if (intersection?.isIntersecting) {
      setIsVisible(true)
    }
  }, [intersection])

  return (
    <Layout
      theme={theme}
      seo={{
        title: 'Xeno Robotics',
        description:
          'Xeno Robotics is a pioneering engineering firm fusing state-of-the-art robotics with blockchain-driven intelligence to deliver autonomous systems that adapt, learn, and collaborate in diverse environments.',
      }}
      className={s.home}
    >
      <div className={s.canvas}>
        <WebGL />
      </div>

      {/* <Header /> */}

      <section className={s.hero}>
        <div className="layout-grid-inner" style={{ textAlign: 'center' }}>
          {/* <Title className={s.title} /> */}
          <div className={s.flexcontainer}>
            <AppearTitle>
              <Image className={s.logoimg} />
            </AppearTitle>
          </div>
          {/* <SFDR className={cn(s.icon, introOut && s.show)} /> */}
          <span className={cn(s.sub)}>
            <HeroTextIn introOut={introOut}>
              <h2 className={cn('h3', s.subtitle)}>
                Own the <span style={{ color: '#0097FE' }}>Future</span> of
                Robotics
              </h2>
            </HeroTextIn>
            <HeroTextIn introOut={introOut}>
              <h2 className={cn('p-xs', s.tm)}>Powered by Blockchain</h2>
            </HeroTextIn>

            {/* Made in UAE Badge - Enhanced with proper styling */}
            <div className={cn(s['made-in-uae'], introOut && s.show)}>
              <HeroTextIn introOut={introOut}>
                <div className={s['uae-card']}>
                  <div className={s['badge-container']}>
                    <Image
                      src="/madeinuae-logo-white.png"
                      alt="Made In UAE"
                      width={120}
                      height={60}
                      className={s['uae-logo']}
                    />
                  </div>
                </div>
              </HeroTextIn>
            </div>
          </span>
        </div>

        <div className={cn(s.bottom, 'layout-grid')}>
          <div
            className={cn(
              'hide-on-mobile',
              s['scroll-hint'],
              hasScrolled && s.hide,
              introOut && s.show
            )}
          >
            <div className={s.text}>
              <HeroTextIn introOut={introOut}>
                <p>scroll</p>
              </HeroTextIn>
              <HeroTextIn introOut={introOut}>
                <p> to explore</p>
              </HeroTextIn>
            </div>
          </div>

          <h1 className={cn(s.description, 'p-s')}>
            <HeroTextIn introOut={introOut}>
              <p className={s.text}>
                Built in Dubai, XENO delivers smart robots and Web3-enabled
                hardware you can customize, earn from, and control — all from a
                single unified platform.
              </p>
            </HeroTextIn>
          </h1>
          <Button
            className={cn(s.cta, introOut && s.in)}
            arrow
            target="_blank"
            href="https://www.instagram.com/"
          >
            Order Now
          </Button>
        </div>
      </section>
      <section
        className={s.why}
        data-lenis-scroll-snap-align="start"
        ref={whyRef}
      >
        <div className="layout-grid">
          <h2 className={cn(s.sticky, 'h2')}>
            <AppearTitle>About XENO</AppearTitle>
          </h2>
          <aside className={s.features}>
            <div className={s.feature}>
              <p className="p">
                XENO is a{' '}
                <span style={{ color: '#0097FE' }}>
                  Dubai-based robotics and intelligent hardware company
                </span>{' '}
                reimagining how people interact with automation. We design and
                deliver purpose-built robots, smart products, and token-based
                infrastructure that gives users true ownership, personalization,
                and income potential.
              </p>
              <br></br>
              <br></br>
              <p className="p">
                Whether you're customizing a robot for your brand, co-owning a
                robot on rent, or staking tokens into our manufacturing roadmap,
                you're not just buying a product — you're building the
                infrastructure of tomorrow.
              </p>
            </div>
            <div className={s.feature}>
              <h3 className={cn(s.title, 'h4')}>XENO Shop</h3>
              <p className="p">
                Our curated robotics catalog features ready-made robots with
                specific functionalities — from hospitality and delivery to
                surveillance and entertainment.
                <br></br>
                <br></br>
                No engineering required. Simply: Choose a robot based on its
                function, customize its look (logo, name, color theme), place
                your order and we'll build, program, and ship it directly to
                you.
                <br></br>
                <br></br>
                We also offer Web3-native smart devices like our upcoming
                crypto-alert speaker, which keeps you informed about wallet
                activity, DAO proposals, and more in real time.
              </p>
            </div>
            <div className={s.feature}>
              <h3 className={cn(s.title, 'h4')}>Robots on Rent (ROR Model)</h3>
              <p className="p">
                Want to earn passive income from the robotics economy? XENO lets
                you co-fund robots that get rented to businesses — restaurants,
                hotels, offices, and beyond.
                <br></br>
                <br></br>
                You provide a portion of the robot's funding, the robot is
                deployed and rented monthly, and you earn rental income over the
                contract term. Everything is contractually tracked and backed by
                XENO.
              </p>
            </div>
            <div className={s.feature}>
              <h3 className={cn(s.title, 'h4')}>XENO DAO</h3>
              <p className="p">
                Token holders propose, vote on, and approve new product
                prototypes. Every approved idea goes through prototyping, IP
                filing, and manufacturing — and the revenue gets shared.
                <br></br>
                This isn't just voting. It's governance with real-world stakes
                and product ownership.
              </p>
            </div>
          </aside>
        </div>
      </section>
      <section ref={featuresRef} className={s.rethink}>
        <div className={cn('layout-grid', s.pre)}>
          <div className={s.highlight} data-lenis-scroll-snap-align="start">
            <Parallax speed={-0.5}>
              <p className="h2">
                <AppearTitle>XENO</AppearTitle>
                <br></br>
                <AppearTitle>Token </AppearTitle>
                <AppearTitle>Utility</AppearTitle>
              </p>
            </Parallax>
          </div>
          <div className={s.comparison}>
            <Parallax speed={0.5}>
              <h3 className={cn(s.title, 'h4')}>
                Unlock the <span style={{ color: '#0097FE' }}>XENOverse</span>
              </h3>
              <br></br>
              <p className="p">
                The XENO token is your gateway to full ecosystem access. Buy,
                use, and hold — it's the key to unlocking the robotic
                infrastructure revolution.
              </p>
            </Parallax>
          </div>
        </div>
      </section>
      <section ref={cardsRectRef}>
        <FeatureCards />
      </section>

      <section ref={featuresRef} className={s.rethink}>
        <div
          className={s.cards}
          ref={cardsRef}
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <h2
            className="h2"
            style={{ textAlign: 'center', marginBottom: '20px' }}
          >
            OUR PARTNERS
          </h2>
          <PartnersMarquee />
        </div>
      </section>

      <section
        ref={(node) => {
          zoomWrapperRectRef(node)
          zoomRef.current = node
        }}
        className={s.solution}
      >
        <div className={s.inner}>
          <div className={s.zoom}>
            <h2 className={cn(s.first, 'h1 vh')}>
              IT'S MORE <br />
              <span className="contrast">THAN JUST ROBOTS</span>
            </h2>
            <h2 className={cn(s.enter, 'h3 vh')}>ECOSYSTEM</h2>
            <h2 className={cn(s.second, 'h1 vh')}>IT'S A WEB3-ELEVATED</h2>
          </div>
        </div>
      </section>

      <section
        ref={(node) => {
          inuseRef(node)
          inUseRef.current = node
          whiteRef
        }}
        className={cn(
          s['in-use'],
          'theme-light',
          s.featuring,
          visible && s.visible
        )}
      >
        <div style={{ height: '10vh' }}></div>
        <div className="layout-grid">
          <aside className={s.title}>
            <p className="h3">Press Mentions</p>
          </aside>
          <ul className={s.list}>
            {projects.map(({ title, source, href }, i) => (
              <li key={i}>
                <ListItem
                  title={title}
                  source={source}
                  href={href}
                  index={i}
                  visible={visible}
                />
              </li>
            ))}
          </ul>
        </div>
      </section>
      {/* <section className={cn('theme-light', s.featuring)} ref={whiteRef}>
        <div className={s.inner}>
          <div className={cn('layout-block', s.intro)}>
            <p className="p-l">
              We are here to ignite a thrilling revolution in India's
              entertainment sector with our groundbreaking park.
              <br />
              <span
                className="contrast semi-bold"
              >
                Our mission?
              </span>
              <br />
              To provide a one-stop, adrenaline-pumping adventure for all ages
            </p>
          </div>
        </div>
      </section> */}

      <style jsx>{`
        /* Made in UAE Badge Styles - Enhanced for prominence */

        .${s['made-in-uae']} {
          opacity: 0;
          transition: all 1s cubic-bezier(0.16, 1, 0.3, 1);
          transition-delay: 0.6s;
          grid-column: 2;
          justify-self: center;
          transform: translateY(20px);
        }

        .${s['made-in-uae']}.${s.show} {
          opacity: 1;
          transform: translateY(0);
        }

        .${s['badge-container']} {
          display: flex;
          align-items: center;
          gap: 16px;
          padding: 16px 24px;
          background: linear-gradient(
            135deg,
            rgba(255, 255, 255, 0.1),
            rgba(255, 255, 255, 0.05)
          );
          border: 2px solid rgba(0, 151, 254, 0.3);
          border-radius: 24px;
          backdrop-filter: blur(20px);
          transition: all 0.4s ease;
          box-shadow: 0 8px 32px rgba(0, 151, 254, 0.1),
            inset 0 1px 0 rgba(255, 255, 255, 0.1);
          position: relative;
          overflow: hidden;
        }

        .${s['badge-container']}::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(
            90deg,
            transparent,
            rgba(0, 151, 254, 0.1),
            transparent
          );
          transition: left 0.6s ease;
        }

        .${s['badge-container']}:hover {
          background: linear-gradient(
            135deg,
            rgba(255, 255, 255, 0.15),
            rgba(255, 255, 255, 0.08)
          );
          border-color: rgba(0, 151, 254, 0.5);
          transform: translateY(-4px) scale(1.02);
          box-shadow: 0 16px 48px rgba(0, 151, 254, 0.2),
            inset 0 1px 0 rgba(255, 255, 255, 0.2);
        }

        .${s['badge-container']}:hover::before {
          left: 100%;
        }

        .${s['uae-logo']} {
          border-radius: 16px !important;
          object-fit: contain !important;
          flex-shrink: 0 !important;
          width: 60px !important;
          filter: drop-shadow(0 8px 24px rgba(0, 151, 254, 0.3));
          transition: all 0.4s ease !important;
          background: rgba(255, 255, 255, 0);
          backdrop-filter: blur(12.6px);
          -webkit-backdrop-filter: blur(12.6px);
          border: 1px solid rgba(255, 255, 255, 0.38);
          padding: 16px;
          box-shadow: 0 4px 30px rgba(0, 0, 0, 0.1);
        }

        .${s['badge-container']}:hover .${s['uae-logo']} {
          transform: scale(1.05);
          filter: drop-shadow(0 6px 16px rgba(0, 151, 254, 0.3));
        }

        .${s['badge-content']} {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }

        .${s['badge-text']} {
          font-size: 1.125rem;
          font-weight: 700;
          color: rgba(255, 255, 255, 0.95);
          white-space: nowrap;
          letter-spacing: 0.5px;
          text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
        }

        .${s['badge-subtext']} {
          font-size: 0.875rem;
          font-weight: 500;
          color: rgba(0, 151, 254, 0.9);
          white-space: nowrap;
          letter-spacing: 0.25px;
        }

        /* Responsive Styles */
        @media (max-width: 1024px) {
          .${s['made-in-uae']} {
            grid-column: 1 / -1;
            justify-self: center;
            margin: 2rem 0;
          }

          .${s['uae-logo']} {
            width: 56px !important;
            height: 56px !important;
          }

          .${s['badge-text']} {
            font-size: 1rem;
          }

          .${s['badge-subtext']} {
            font-size: 0.8rem;
          }
        }

        @media (max-width: 768px) {
          .${s['badge-container']} {
            padding: 12px 20px;
            gap: 12px;
            border-radius: 20px;
          }

          .${s['uae-logo']} {
            width: 48px !important;
            height: 48px !important;
          }

          .${s['badge-text']} {
            font-size: 0.95rem;
          }

          .${s['badge-subtext']} {
            font-size: 0.75rem;
          }
        }

        @media (max-width: 480px) {
          .${s['badge-container']} {
            padding: 10px 16px;
            gap: 10px;
            border-radius: 16px;
          }

          .${s['uae-logo']} {
            width: 40px !important;
            height: 40px !important;
          }

          .${s['badge-text']} {
            font-size: 0.875rem;
          }

          .${s['badge-subtext']} {
            font-size: 0.7rem;
          }
        }

        /* Theme variations for light mode */
        :global(.theme-light) .${s['made-in-uae']} .${s['badge-container']} {
          background: linear-gradient(
            135deg,
            rgba(0, 0, 0, 0.08),
            rgba(0, 0, 0, 0.03)
          );
          border-color: rgba(0, 151, 254, 0.4);
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1),
            inset 0 1px 0 rgba(255, 255, 255, 0.8);
        }

        :global(.theme-light)
          .${s['made-in-uae']}
          .${s['badge-container']}:hover {
          background: linear-gradient(
            135deg,
            rgba(0, 0, 0, 0.12),
            rgba(0, 0, 0, 0.06)
          );
          border-color: rgba(0, 151, 254, 0.6);
          box-shadow: 0 16px 48px rgba(0, 0, 0, 0.15),
            inset 0 1px 0 rgba(255, 255, 255, 0.9);
        }

        :global(.theme-light) .${s['made-in-uae']} .${s['badge-text']} {
          color: rgba(0, 0, 0, 0.9);
          text-shadow: 0 1px 2px rgba(255, 255, 255, 0.8);
        }

        :global(.theme-light) .${s['made-in-uae']} .${s['badge-subtext']} {
          color: rgba(0, 151, 254, 0.8);
        }

        /* Premium floating animation */
        @keyframes premiumFloat {
          0%,
          100% {
            transform: translateY(0px) rotate(0deg);
          }
          25% {
            transform: translateY(-2px) rotate(0.5deg);
          }
          50% {
            transform: translateY(-4px) rotate(0deg);
          }
          75% {
            transform: translateY(-2px) rotate(-0.5deg);
          }
        }

        @keyframes glowPulse {
          0%,
          100% {
            box-shadow: 0 8px 32px rgba(0, 151, 254, 0.1),
              inset 0 1px 0 rgba(255, 255, 255, 0.1);
          }
          50% {
            box-shadow: 0 8px 32px rgba(0, 151, 254, 0.2),
              inset 0 1px 0 rgba(255, 255, 255, 0.15);
          }
        }

        .${s['made-in-uae']}.${s.show} .${s['badge-container']} {
          animation: premiumFloat 4s ease-in-out infinite,
            glowPulse 3s ease-in-out infinite;
          animation-delay: 3s, 4s;
        }

        /* Additional premium effects */
        .${s['badge-container']}::after {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          border-radius: 24px;
          background: linear-gradient(
            135deg,
            rgba(0, 151, 254, 0.1),
            transparent
          );
          opacity: 0;
          transition: opacity 0.3s ease;
          pointer-events: none;
        }

        .${s['badge-container']}:hover::after {
          opacity: 1;
        }
      `}</style>
    </Layout>
  )
}

export async function getStaticProps() {
  return {
    props: {
      id: 'home',
    }, // will be passed to the page component as props
  }
}
